import React, { useMemo, useEffect, useState } from 'react';
import { Box, styled } from '@mui/material';

/** Song interface */
export interface Song {
	name: string;
	lyrics: string;
}

/** Split each song’s lyrics by newlines and gather non-empty lines. */
function getAllLyricLines(songs: Song[]): string[] {
	const lines: string[] = [];
	songs.forEach((song) => {
		const splitted = song.lyrics.split('\n').map((l) => l.trim());
		splitted.forEach((line) => {
			if (line) lines.push(line);
		});
	});
	return lines;
}

/** Simple random pick. */
function pickRandom<T>(array: T[]): T {
	return array[Math.floor(Math.random() * array.length)];
}

/** 
 * Create a snippet of 2–6 words from a line,
 * optionally inserting a comma. 
 * Remove trailing punctuation, skip if "אז" or empty.
 */
/**
 * Splits a line by punctuation, picks a random phrase, and cleans up trailing punctuation.
 * The chosen snippet length is effectively determined by the punctuation in the text.
 */
function createSnippetFromLine(line: string): string {
	// Remove quotes
	let cleaned = line.replace(/["']/g, '');

	// Split by punctuation that could separate "sentences" or sub-phrases: . , ; ? !
	// Trim each piece, then remove empty
	let phrases = cleaned
		.split(/[.,;?!]+/)
		.map((phrase) => phrase.trim())
		.filter(Boolean);

	// If there's nothing left, bail
	if (phrases.length === 0) return '';

	// Pick a random phrase
	let snippet = pickRandom(phrases);

	// Remove any trailing punctuation from snippet (just in case)
	snippet = snippet.replace(/[.,/#!$%^&*;:{}=\-_`~()]+$/, '').trim();

	// Skip if snippet is "" or "אז"
	if (snippet === '' || snippet === 'אז') return '';

	return snippet;
}


/** 
 * Returns a snippet object with text + whether it's a title, 
 * or null if we fail. 
 */
function pickRandomSnippet(titles: string[], lines: string[]): { text: string; isTitle: boolean } | null {
	// 20% for title, 80% for lyrics
	const fromTitle = Math.random() < 0.2;

	if (fromTitle && titles.length > 0) {
		let chosen = pickRandom(titles);
		chosen = chosen.replace(/["']/g, ''); // remove quotes
		chosen = chosen.replace(/[.,/#!$%^&*;:{}=\-_`~()]+$/, '').trim();
		if (chosen === '' || chosen === 'אז') return null;
		return { text: chosen, isTitle: true };
	} else if (lines.length > 0) {
		for (let i = 0; i < 5; i++) {
			const line = pickRandom(lines);
			const snippet = createSnippetFromLine(line);
			if (snippet) return { text: snippet, isTitle: false };
		}
	}
	return null;
}

interface FallingWordsProps {
	songs: Song[];
}

interface FallingItem {
	id: number;
	text: string;
	isTitle: boolean;
	left: string;
	top: string;
	duration: number;
}

/** Keyframe-based animation for falling. */
const FallingWord = styled(Box)(({ theme }) => ({
	position: 'absolute',
	fontSize: '1.4rem',
	opacity: 1,
	direction: 'rtl', // right-to-left text
	animation: 'fall linear forwards',
	whiteSpace: 'nowrap', // ensure snippet stays on one line

	'@keyframes fall': {
		'0%': {
			transform: 'translateY(0)',
			opacity: 1,
		},
		'100%': {
			transform: 'translateY(120vh)', // move below viewport
			opacity: 0,
		},
	},
}));

const Background: React.FC<FallingWordsProps> = ({ songs }) => {
	const [fallingItems, setFallingItems] = useState<FallingItem[]>([]);

	// Gather lines & titles once
	const allLines = useMemo(() => getAllLyricLines(songs), [songs]);
	const allTitles = useMemo(() => songs.map((s) => s.name), [songs]);

	/**
	 * Spawns a single new text item
	 * @param initial If true, we randomize top from -5..50%. Otherwise from -10..-5% (above the screen).
	 */
	const spawnFallingItem = (initial: boolean) => {
		const snippetObj = pickRandomSnippet(allTitles, allLines);
		if (!snippetObj) return;

		const { text, isTitle } = snippetObj;

		// Random horizontal (0..90%) this means it can start off-screen to the right
		const randomLeft = Math.floor(Math.random() * 90);

		// For initial items, let them appear around -5..50%,
		// for subsequent items, appear above the screen (-10..-5%).
		let topMin = -10;
		let topMax = -5;
		if (initial) {
			topMin = -5;
			topMax = 50;
		}
		const randomTop = Math.floor(Math.random() * (topMax - topMin + 1)) + topMin;

		// Duration 5..15 for variety
		const duration = 8 + Math.random() * 10;

		const newItem: FallingItem = {
			id: Math.random(),
			text,
			isTitle,
			left: `${randomLeft}%`,
			top: `${randomTop}vh`,
			duration,
		};
		setFallingItems((prev) => [...prev, newItem]);
	};

	/** On mount, spawn an initial batch. */
	useEffect(() => {
		const initialCount = 6;
		for (let i = 0; i < initialCount; i++) {
			spawnFallingItem(true);
		}

		// Then spawn multiple items every 2s
		const interval = setInterval(() => {
			// let's spawn 3 items each time after the initial
			for (let i = 0; i < 3; i++) {
				spawnFallingItem(false);
			}
		}, 2000);

		return () => clearInterval(interval);
	}, [allLines, allTitles]);

	return (
		<Box
			sx={{
				position: 'fixed',
				width: '100vw',
				height: '100vh',
				top: 0,
				left: 0,
				zIndex: -1,
				overflow: 'hidden',
				backgroundColor: '#fdfdfd',
			}}
		>
			{fallingItems.map((item) => (
				<FallingWord
					key={item.id}
					sx={{
						left: item.left,
						color: 'rgba(0,0,0,0.57)',
						top: item.top,
						animationDuration: `${item.duration}s`,
						fontFamily: "Amatic SC",
						// Shine text if it's a title
						textShadow: item.isTitle
							? '0 0 6px rgba(236, 163, 163, 0.45), 0 0 5px rgba(236, 163, 163, 0.6)'
							: 'none',
						fontSize: item.isTitle ? '1.5rem' : '1.4rem',
					}}
				>
					{item.text}
				</FallingWord>
			))}
		</Box>
	);
};

export default Background;
